import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Network, Plus, Zap, CheckCircle2, XCircle, Clock,
  ArrowUpRight, ArrowDownLeft, AlertCircle, X,
  Activity, ShieldCheck, Radio,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import api from '../../services/api';

// ─── Types ───────────────────────────────────────────────────────────────────
interface OutboundConnection {
  id: string; targetName: string; endpoint: string; authType: string;
  circuitState: 'CLOSED' | 'OPEN' | 'HALF_OPEN';
  status: 'ONLINE' | 'OFFLINE' | 'DEGRADED';
  latencyMs: number | null;
  lastPingAt: string | null;
  failureCount: number; successCount: number;
}

interface InboundWebhook {
  id: string; sourceName: string; webhookUrl: string;
  secret: string | null; isActive: boolean;
  lastReceivedAt: string | null; receivedCount: number;
  lastPayloadType: string | null;
}

interface NdopInboundEvent {
  id: string; receivedAt: string; sourceName: string;
  payloadType: 'OFFICIAL_RESULT' | 'ACKNOWLEDGEMENT' | 'REQUEST_RESUBMIT' | 'STATUS_UPDATE';
  sessionCode: string | null;
  officialScore: number | null; previewScore: number | null;
  hasDivergence: boolean; processed: boolean;
}

// ─── Mock data ────────────────────────────────────────────────────────────────
const MOCK_OUTBOUND: OutboundConnection[] = [
  {
    id: 'conn-001', targetName: 'NDOP', endpoint: 'https://ndop.gov.vn/api/v1',
    authType: 'SIGNED_JWT', circuitState: 'CLOSED', status: 'ONLINE',
    latencyMs: 142, lastPingAt: new Date(Date.now() - 120_000).toISOString(),
    failureCount: 1, successCount: 847,
  },
  {
    id: 'conn-002', targetName: 'C12_SOC', endpoint: 'https://soc.c12.gov.vn/api',
    authType: 'MTLS', circuitState: 'HALF_OPEN', status: 'DEGRADED',
    latencyMs: 890, lastPingAt: new Date(Date.now() - 600_000).toISOString(),
    failureCount: 3, successCount: 124,
  },
];

const MOCK_WEBHOOKS: InboundWebhook[] = [
  {
    id: 'wh-001', sourceName: 'NDOP — Kết quả chính thức',
    webhookUrl: '/webhooks/ndop/official-result',
    secret: 'wh_***', isActive: true,
    lastReceivedAt: new Date(Date.now() - 3600_000).toISOString(),
    receivedCount: 12, lastPayloadType: 'OFFICIAL_RESULT',
  },
  {
    id: 'wh-002', sourceName: 'C12 Audit — Yêu cầu bổ sung',
    webhookUrl: '/webhooks/c12/audit-request',
    secret: 'wh_***', isActive: true,
    lastReceivedAt: new Date(Date.now() - 86400_000).toISOString(),
    receivedCount: 3, lastPayloadType: 'REQUEST_RESUBMIT',
  },
];

const MOCK_INBOUND_EVENTS: NdopInboundEvent[] = [
  {
    id: 'ev-001', receivedAt: new Date(Date.now() - 3600_000).toISOString(),
    sourceName: 'NDOP', payloadType: 'OFFICIAL_RESULT',
    sessionCode: 'XDA-2026-00012', officialScore: 75.5, previewScore: 72.0,
    hasDivergence: false, processed: true,
  },
  {
    id: 'ev-002', receivedAt: new Date(Date.now() - 7200_000).toISOString(),
    sourceName: 'NDOP', payloadType: 'OFFICIAL_RESULT',
    sessionCode: 'XDA-2026-00009', officialScore: 80.0, previewScore: 69.0,
    hasDivergence: true, processed: true,
  },
  {
    id: 'ev-003', receivedAt: new Date(Date.now() - 172800_000).toISOString(),
    sourceName: 'C12 Audit', payloadType: 'REQUEST_RESUBMIT',
    sessionCode: 'XDA-2026-00007', officialScore: null, previewScore: null,
    hasDivergence: false, processed: false,
  },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────
const TARGET_DEFAULTS: Record<string, string> = {
  NDOP:      'https://ndop.gov.vn/api/v1',
  C12_SOC:   'https://soc.c12.gov.vn/api',
  C12_AUDIT: 'https://audit.c12.gov.vn/api',
};

const statusColor = (s: string) =>
  s === 'ONLINE'   ? 'text-emerald-600 dark:text-emerald-400' :
  s === 'DEGRADED' ? 'text-amber-600 dark:text-amber-400' :
                     'text-rose-600 dark:text-rose-400';

const circuitBadge = (s: string) =>
  s === 'CLOSED'    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400' :
  s === 'OPEN'      ? 'bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-400' :
                      'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400';

const PAYLOAD_LABELS: Record<string, string> = {
  OFFICIAL_RESULT:  'Kết quả chính thức',
  ACKNOWLEDGEMENT:  'Xác nhận nhận',
  REQUEST_RESUBMIT: 'Yêu cầu bổ sung',
  STATUS_UPDATE:    'Cập nhật trạng thái',
};

// ─── Circuit Breaker Diagram ──────────────────────────────────────────────────
function CircuitBreakerDiagram({ state }: { state: 'CLOSED' | 'OPEN' | 'HALF_OPEN' }) {
  const states = ['CLOSED', 'HALF_OPEN', 'OPEN'] as const;
  return (
    <div className="flex items-center gap-2 mt-3">
      {states.map((s, i) => (
        <div key={s} className="flex items-center gap-2">
          <div className={`px-3 py-1.5 rounded-xl text-[10px] font-black transition-all ${
            state === s ? circuitBadge(s) + ' ring-2 ring-current ring-offset-1 scale-105' : 'bg-slate-100 dark:bg-white/5 text-slate-400'
          }`}>{s}</div>
          {i < 2 && <span className="text-slate-300 dark:text-slate-700 text-xs">→</span>}
        </div>
      ))}
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function Connections() {
  const qc = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [target, setTarget]       = useState<'NDOP' | 'C12_SOC' | 'C12_AUDIT'>('NDOP');
  const [endpoint, setEndpoint]   = useState(TARGET_DEFAULTS.NDOP);
  const [authType, setAuthType]   = useState<'SIGNED_JWT' | 'MTLS' | 'API_KEY'>('SIGNED_JWT');
  const [pingId, setPingId]       = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'outbound' | 'inbound' | 'events'>('outbound');

  const { data: connections = MOCK_OUTBOUND, isLoading: loadConn } = useQuery<OutboundConnection[]>({
    queryKey: ['agent-connections'],
    queryFn: () => api.get('/connections').then(r => r.data ?? MOCK_OUTBOUND).catch(() => MOCK_OUTBOUND),
    refetchInterval: 30_000,
  });

  const { data: webhooks = MOCK_WEBHOOKS } = useQuery<InboundWebhook[]>({
    queryKey: ['agent-webhooks'],
    queryFn: () => api.get('/connections/webhooks').then(r => r.data ?? MOCK_WEBHOOKS).catch(() => MOCK_WEBHOOKS),
  });

  const { data: inboundEvents = MOCK_INBOUND_EVENTS } = useQuery<NdopInboundEvent[]>({
    queryKey: ['agent-inbound-events'],
    queryFn: () => api.get('/connections/inbound-events').then(r => r.data ?? MOCK_INBOUND_EVENTS).catch(() => MOCK_INBOUND_EVENTS),
    refetchInterval: 60_000,
  });

  const create = useMutation({
    mutationFn: () => api.post('/connections', { targetName: target, endpoint, authType }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['agent-connections'] }); setShowModal(false); toast.success('Kết nối đã được thêm'); },
    onError: (err: unknown) => {
      const e = err as { response?: { data?: { message?: string } } };
      toast.error(e.response?.data?.message ?? 'Lỗi thêm kết nối');
    },
  });

  const ping = useMutation({
    mutationFn: (id: string) => { setPingId(id); return api.post(`/connections/${id}/ping`); },
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['agent-connections'] });
      const d = r.data;
      toast.success(d?.online ? `ONLINE · ${d.latencyMs}ms` : 'Không thể kết nối');
      setPingId(null);
    },
    onError: () => { toast.error('Ping thất bại'); setPingId(null); },
  });

  const ndopOnline = connections.find(c => c.targetName === 'NDOP');
  const divergenceEvents = inboundEvents.filter(e => e.hasDivergence);

  return (
    <div className="space-y-8">
      {/* ── Header ── */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-black tracking-tight text-slate-900 dark:text-white">
            Kết nối <span className="text-indigo-500">NDOP / C12</span>
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 font-medium">
            Outbound (submit) · Inbound webhook (nhận kết quả chính thức) · Circuit breaker
          </p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 px-5 py-2.5 rounded-2xl bg-indigo-600 text-white text-sm font-black hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-500/20"
        >
          <Plus size={16} /> Thêm kết nối
        </button>
      </div>

      {/* ── Divergence Alert ── */}
      {divergenceEvents.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-4 rounded-2xl bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 flex items-start gap-3"
        >
          <AlertCircle size={18} className="text-amber-500 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-black text-amber-700 dark:text-amber-400">
              Phát hiện {divergenceEvents.length} kết quả có độ lệch &gt; 5 điểm (|preview − official| &gt; 5)
            </p>
            <p className="text-xs text-amber-600 dark:text-amber-500 mt-0.5">
              {divergenceEvents.map(e => e.sessionCode).join(', ')} — cần xem xét nguyên nhân lệch điểm
            </p>
          </div>
        </motion.div>
      )}

      {/* ── Stat chips ── */}
      <div className="grid grid-cols-4 gap-4">
        {[
          {
            label: 'NDOP Gateway',
            value: ndopOnline?.status ?? 'UNKNOWN',
            color: ndopOnline?.status === 'ONLINE' ? 'emerald' : 'rose',
            icon: Radio,
            sub: ndopOnline ? `${ndopOnline.latencyMs ?? '?'}ms` : 'Chưa kết nối',
          },
          {
            label: 'Circuit Breaker',
            value: ndopOnline?.circuitState ?? '—',
            color: ndopOnline?.circuitState === 'CLOSED' ? 'emerald' : ndopOnline?.circuitState === 'HALF_OPEN' ? 'amber' : 'rose',
            icon: Activity,
            sub: 'Ngưỡng: 3 lỗi → OPEN',
          },
          {
            label: 'Inbound Webhooks',
            value: `${webhooks.filter(w => w.isActive).length} / ${webhooks.length} active`,
            color: 'indigo',
            icon: ArrowDownLeft,
            sub: `${inboundEvents.length} sự kiện nhận`,
          },
          {
            label: 'Độ lệch điểm',
            value: `${divergenceEvents.length} ca`,
            color: divergenceEvents.length > 0 ? 'amber' : 'emerald',
            icon: ShieldCheck,
            sub: '|preview − official| > 5',
          },
        ].map((c, i) => (
          <motion.div
            key={c.label}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.07 }}
            className="bg-white dark:bg-white/[0.03] border border-slate-200 dark:border-white/5 rounded-3xl p-5"
          >
            <div className={`w-10 h-10 rounded-2xl flex items-center justify-center mb-3 bg-${c.color}-100 dark:bg-${c.color}-500/15`}>
              <c.icon size={18} className={`text-${c.color}-600 dark:text-${c.color}-400`} />
            </div>
            <p className="text-lg font-black text-slate-900 dark:text-white">{c.value}</p>
            <p className="text-[10px] text-slate-400 font-bold mt-0.5 uppercase tracking-wider">{c.label}</p>
            <p className={`text-[10px] mt-1 font-bold text-${c.color}-600 dark:text-${c.color}-400`}>{c.sub}</p>
          </motion.div>
        ))}
      </div>

      {/* ── Tabs ── */}
      <div className="flex gap-1 bg-slate-100 dark:bg-white/5 p-1 rounded-2xl w-fit">
        {([
          { key: 'outbound' as const, label: `Outbound (${connections.length})`, icon: ArrowUpRight },
          { key: 'inbound' as const, label: `Inbound Webhooks (${webhooks.length})`, icon: ArrowDownLeft },
          { key: 'events' as const, label: `Sự kiện nhận (${inboundEvents.length})`, icon: Activity },
        ]).map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-black transition-all ${
              activeTab === tab.key
                ? 'bg-white dark:bg-white/10 text-slate-900 dark:text-white shadow-sm'
                : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
            }`}
          >
            <tab.icon size={12} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── OUTBOUND ── */}
      {activeTab === 'outbound' && (
        <div className="space-y-4">
          {/* Circuit Breaker explanation */}
          <div className="p-4 rounded-2xl bg-slate-50 dark:bg-white/[0.02] border border-slate-200 dark:border-white/5 text-xs text-slate-600 dark:text-slate-400 font-medium">
            <strong className="text-slate-900 dark:text-white">Circuit Breaker:</strong>
            {' '}CLOSED (bình thường) → nếu lỗi 3 lần → OPEN (ngừng gửi, timeout 5 phút) → HALF_OPEN (thử 1 request) → CLOSED hoặc OPEN lại.
          </div>

          {loadConn ? (
            <div className="text-center py-20 text-slate-400">Đang tải...</div>
          ) : !connections.length ? (
            <div className="text-center py-20">
              <Network size={48} className="mx-auto text-slate-300 dark:text-slate-700 mb-4" />
              <p className="text-slate-400 font-medium">Chưa có kết nối nào.</p>
            </div>
          ) : (
            <div className="grid gap-6 md:grid-cols-2">
              {connections.map((conn, i) => (
                <motion.div
                  key={conn.id}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.08 }}
                  className="bg-white dark:bg-white/[0.03] border border-slate-200 dark:border-white/5 rounded-3xl p-6 shadow-sm"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <ArrowUpRight size={16} className="text-indigo-500" />
                        <span className="font-black text-slate-900 dark:text-white">{conn.targetName}</span>
                      </div>
                      <p className="text-xs text-slate-400 font-mono truncate max-w-[220px]">{conn.endpoint}</p>
                      <p className="text-[10px] text-slate-400 mt-0.5">Auth: {conn.authType}</p>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <span className={`text-sm font-black flex items-center gap-1.5 ${statusColor(conn.status)}`}>
                        {conn.status === 'ONLINE' ? <CheckCircle2 size={14} /> : <XCircle size={14} />}
                        {conn.status}
                      </span>
                    </div>
                  </div>

                  {/* Circuit Breaker visual */}
                  <CircuitBreakerDiagram state={conn.circuitState} />

                  <div className="grid grid-cols-3 gap-3 mt-4 mb-4 text-center">
                    <div className="bg-slate-50 dark:bg-white/[0.02] rounded-2xl p-3">
                      <p className="text-[9px] text-slate-400 font-black mb-0.5 uppercase">Latency</p>
                      <p className="text-sm font-black text-slate-900 dark:text-white">{conn.latencyMs ? `${conn.latencyMs}ms` : '—'}</p>
                    </div>
                    <div className="bg-slate-50 dark:bg-white/[0.02] rounded-2xl p-3">
                      <p className="text-[9px] text-slate-400 font-black mb-0.5 uppercase">Success</p>
                      <p className="text-sm font-black text-emerald-600 dark:text-emerald-400">{conn.successCount}</p>
                    </div>
                    <div className="bg-slate-50 dark:bg-white/[0.02] rounded-2xl p-3">
                      <p className="text-[9px] text-slate-400 font-black mb-0.5 uppercase">Failures</p>
                      <p className="text-sm font-black text-rose-600 dark:text-rose-400">{conn.failureCount}</p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-400 flex items-center gap-1.5 font-medium">
                      <Clock size={11} />
                      {conn.lastPingAt ? new Date(conn.lastPingAt).toLocaleTimeString('vi-VN') : 'Chưa ping'}
                    </span>
                    <button
                      onClick={() => ping.mutate(conn.id)}
                      disabled={pingId === conn.id}
                      className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 text-xs font-black hover:bg-indigo-100 dark:hover:bg-indigo-500/20 transition-all disabled:opacity-50"
                    >
                      <Zap size={12} className={pingId === conn.id ? 'animate-pulse' : ''} />
                      {pingId === conn.id ? 'Đang ping…' : 'Ping'}
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── INBOUND WEBHOOKS ── */}
      {activeTab === 'inbound' && (
        <div className="space-y-4">
          <div className="p-4 rounded-2xl bg-blue-50 dark:bg-blue-500/5 border border-blue-100 dark:border-blue-500/20 text-xs text-blue-700 dark:text-blue-400 font-medium">
            <strong>Inbound webhooks</strong>: NDOP và C12 gửi kết quả chính thức về Agent Node qua HTTPS POST có ký HMAC-SHA256.
            Agent Node xác minh chữ ký, lưu vào DB, và so sánh với preview score.
          </div>

          {webhooks.map((wh, i) => (
            <motion.div
              key={wh.id}
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.06 }}
              className="bg-white dark:bg-white/[0.03] border border-slate-200 dark:border-white/5 rounded-3xl p-6"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3">
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${wh.isActive ? 'bg-emerald-100 dark:bg-emerald-500/15' : 'bg-slate-100 dark:bg-white/5'}`}>
                    <ArrowDownLeft size={16} className={wh.isActive ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-400'} />
                  </div>
                  <div>
                    <p className="font-black text-slate-900 dark:text-white text-sm">{wh.sourceName}</p>
                    <p className="text-xs font-mono text-indigo-600 dark:text-indigo-400 mt-0.5">{wh.webhookUrl}</p>
                    {wh.secret && <p className="text-[10px] text-slate-400 mt-0.5">Secret: {wh.secret}</p>}
                  </div>
                </div>
                <span className={`text-[10px] font-black px-2 py-1 rounded-full ${wh.isActive ? 'bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-400' : 'bg-slate-100 dark:bg-white/5 text-slate-400'}`}>
                  {wh.isActive ? 'ACTIVE' : 'INACTIVE'}
                </span>
              </div>
              <div className="flex items-center gap-6 mt-4 text-xs text-slate-500">
                <span className="flex items-center gap-1.5">
                  <Activity size={11} />
                  {wh.receivedCount} sự kiện nhận
                </span>
                {wh.lastReceivedAt && (
                  <span className="flex items-center gap-1.5">
                    <Clock size={11} />
                    {new Date(wh.lastReceivedAt).toLocaleString('vi-VN')}
                  </span>
                )}
                {wh.lastPayloadType && (
                  <span className="text-indigo-500 font-bold">{PAYLOAD_LABELS[wh.lastPayloadType] ?? wh.lastPayloadType}</span>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* ── INBOUND EVENTS ── */}
      {activeTab === 'events' && (
        <div className="space-y-3">
          {inboundEvents.length === 0 ? (
            <div className="text-center py-12 text-slate-400">Chưa có sự kiện inbound</div>
          ) : (
            inboundEvents.map((ev, i) => (
              <motion.div
                key={ev.id}
                initial={{ opacity: 0, x: -6 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.05 }}
                className={`p-4 rounded-2xl border ${
                  ev.hasDivergence
                    ? 'bg-amber-50 dark:bg-amber-500/5 border-amber-200 dark:border-amber-500/20'
                    : 'bg-white dark:bg-white/[0.02] border-slate-200 dark:border-white/5'
                }`}
              >
                <div className="flex items-start gap-4">
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${
                    ev.payloadType === 'OFFICIAL_RESULT'
                      ? 'bg-indigo-100 dark:bg-indigo-500/15'
                      : ev.payloadType === 'REQUEST_RESUBMIT'
                      ? 'bg-rose-100 dark:bg-rose-500/15'
                      : 'bg-slate-100 dark:bg-white/5'
                  }`}>
                    <ArrowDownLeft size={16} className={
                      ev.payloadType === 'OFFICIAL_RESULT' ? 'text-indigo-600 dark:text-indigo-400' :
                      ev.payloadType === 'REQUEST_RESUBMIT' ? 'text-rose-500' : 'text-slate-400'
                    } />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-black text-slate-900 dark:text-white">
                        {PAYLOAD_LABELS[ev.payloadType] ?? ev.payloadType}
                      </span>
                      {ev.sessionCode && (
                        <span className="text-[9px] font-mono text-indigo-600 dark:text-indigo-400">{ev.sessionCode}</span>
                      )}
                      {ev.hasDivergence && (
                        <span className="text-[9px] font-black px-2 py-0.5 rounded-full bg-amber-500 text-white uppercase">
                          ΔScore &gt; 5
                        </span>
                      )}
                      {!ev.processed && (
                        <span className="text-[9px] font-black px-2 py-0.5 rounded-full bg-rose-100 dark:bg-rose-500/20 text-rose-600 dark:text-rose-400">
                          Chưa xử lý
                        </span>
                      )}
                    </div>

                    {ev.officialScore != null && (
                      <div className="flex items-center gap-4 mt-1.5 text-xs">
                        <span className="text-slate-500">Official: <strong className="text-indigo-600 dark:text-indigo-400">{ev.officialScore}</strong></span>
                        {ev.previewScore != null && (
                          <span className="text-slate-500">Preview: <strong className="text-slate-700 dark:text-slate-300">{ev.previewScore}</strong></span>
                        )}
                        {ev.hasDivergence && (
                          <span className="font-black text-amber-600 dark:text-amber-400">
                            Δ = {Math.abs((ev.officialScore ?? 0) - (ev.previewScore ?? 0)).toFixed(1)}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-[10px] text-slate-400">{ev.sourceName}</p>
                    <p className="text-[10px] text-slate-400 mt-0.5">{new Date(ev.receivedAt).toLocaleString('vi-VN')}</p>
                  </div>
                </div>
              </motion.div>
            ))
          )}
        </div>
      )}

      {/* ── Add Connection Modal ── */}
      <AnimatePresence>
        {showModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white dark:bg-[#161617] border border-slate-200 dark:border-white/10 rounded-3xl p-8 w-full max-w-md shadow-2xl"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-black text-slate-900 dark:text-white">Thêm kết nối outbound</h2>
                <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-white"><X size={18} /></button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="text-xs font-black text-slate-500 uppercase tracking-wider block mb-2">Target</label>
                  <select
                    value={target}
                    onChange={e => { setTarget(e.target.value as 'NDOP' | 'C12_SOC' | 'C12_AUDIT'); setEndpoint(TARGET_DEFAULTS[e.target.value]); }}
                    className="w-full px-4 py-3 rounded-2xl bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 text-sm font-medium outline-none focus:border-indigo-500 dark:text-white"
                  >
                    <option value="NDOP">NDOP — Nền tảng điều phối dữ liệu</option>
                    <option value="C12_SOC">C12 SOC — Giám sát an ninh</option>
                    <option value="C12_AUDIT">C12 Audit — Kiểm toán đột xuất</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-black text-slate-500 uppercase tracking-wider block mb-2">Endpoint URL</label>
                  <input
                    value={endpoint}
                    onChange={e => setEndpoint(e.target.value)}
                    className="w-full px-4 py-3 rounded-2xl bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 text-sm font-medium outline-none focus:border-indigo-500 dark:text-white"
                  />
                </div>
                <div>
                  <label className="text-xs font-black text-slate-500 uppercase tracking-wider block mb-2">Xác thực</label>
                  <select
                    value={authType}
                    onChange={e => setAuthType(e.target.value as 'SIGNED_JWT' | 'MTLS' | 'API_KEY')}
                    className="w-full px-4 py-3 rounded-2xl bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 text-sm font-medium outline-none focus:border-indigo-500 dark:text-white"
                  >
                    <option value="SIGNED_JWT">Signed JWT (khuyến nghị)</option>
                    <option value="MTLS">Mutual TLS</option>
                    <option value="API_KEY">API Key</option>
                  </select>
                </div>
              </div>
              <div className="flex gap-3 mt-8">
                <button
                  onClick={() => setShowModal(false)}
                  className="flex-1 py-3 rounded-2xl bg-slate-100 dark:bg-white/5 text-slate-600 dark:text-slate-400 font-black text-sm"
                >
                  Huỷ
                </button>
                <button
                  onClick={() => create.mutate()}
                  disabled={create.isPending || !endpoint}
                  className="flex-1 py-3 rounded-2xl bg-indigo-600 text-white font-black text-sm hover:bg-indigo-700 transition-all disabled:opacity-50"
                >
                  {create.isPending ? 'Đang lưu…' : 'Thêm kết nối'}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
